import React, { useEffect, useState } from "react";
import propTypes from "prop-types";

import { FormLabel, TextField } from "@material-ui/core";

import { getControlId } from "utils/utils.js";

import ErrorLabel from "components/ErrorLabel";

import SearchDashboard from "assets/SecondarySideBar/search_icon.svg";
import CancelIcon from "assets/SvgIcon/Cancel";

import DatePopover from "../DatePopover/DatePopover.jsx";

import { inputBoxStyles } from "./input.style";

const InputBox = ({
  id,
  labelText,
  name,
  value,
  onChange,
  placeholder,
  errorMsg,
  requiredMsg,
  blurHandler,
  iscalender = false,
  selectedDate,
  setSelectedDate,
  page,
  disabled,
  rows,
  isSearchBox,
  customClass = "",
  disableIcon,
  validator,
  maxLength,
  type,
  onFocus,
  onEscapeKeyDown,
  isAutoFocus,
  hideClearBtn,
  onMaxLengthReached,
  maxHeight
}) => {
  const classes = inputBoxStyles(this);
  const [inputError, setInputError] = useState();
  const [inputValue, setInputValue] = useState();
  const inputRef = React.useRef(null);
  const [lastValidVal, setLastValue] = useState(value || "");

  useEffect(() => setInputError(errorMsg), [errorMsg]);
  useEffect(() => setInputValue(value), [value]);

  const inputId = id || getControlId(labelText, "input");

  const validateInput = value => {
    if (requiredMsg && !value.trim()) {
      setInputError(errorMsg || requiredMsg);
    } else {
      setInputError(errorMsg);
    }
  };

  const inputChangeHandler = event => {
    let { value, name } = event.target;
    const isSpacesOnly = !value.replace(/\s/g, "").length;
    let textValue = value;
    if (isSpacesOnly) value = value.trim();
    if (validator) {
      if (validator.test(value) || value === "") {
        setLastValue(value);
        setInputValue(value);
      } else {
        textValue = lastValidVal;
        setInputValue(lastValidVal);
      }
    } else {
      setInputValue(value);
    }
    validateInput(textValue);
    if (onChange && onChange instanceof Function) {
      onChange(textValue, name);
    }
  };

  const onBlur = event => {
    const { value } = event.target;
    validateInput(value);
    if (blurHandler) blurHandler(event);
  };

  const onTextFocus = event => {
    if (onFocus) {
      onFocus(event);
    }
  };

  const clearInput = () => {
    setInputValue("");
    const input = inputRef.current;
    const lastValue = input.value;
    input.value = "";
    const event = new Event("input", { bubbles: true });
    const tracker = input._valueTracker;
    if (tracker) {
      tracker.setValue(lastValue);
    }
    input.dispatchEvent(event);
  };

  const onKeyDown = event => {
    if (
      inputRef &&
      event &&
      value &&
      maxLength &&
      typeof value === "string" &&
      value.trim().length === maxLength &&
      typeof onMaxLengthReached === "function"
    ) {
      onMaxLengthReached(maxLength);
    }
  };

  return (
    <>
      <div className={customClass}>
        <FormLabel htmlFor={inputId} className={disabled ? classes.labelDisabled : classes.label}>
          {labelText}
        </FormLabel>
        <TextField
          InputProps={{
            className: classes.textfield,
            disableUnderline: true,
            id: inputId,
            endAdornment: (
              <>
                {iscalender ? (
                  <DatePopover
                    className="padding5"
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    page={page}
                    disableIcon={disableIcon}
                  />
                ) : isSearchBox && !inputValue ? (
                  <img className="padding5" src={SearchDashboard} alt="searchDashboard" />
                ) : (
                  !hideClearBtn &&
                  inputValue &&
                  (!rows || rows === 1) && (
                    <div className="pointer" onClick={clearInput}>
                      <CancelIcon />
                    </div>
                  )
                )}
              </>
            )
          }}
          disabled={disabled}
          autoComplete="off"
          className={classes.input}
          placeholder={placeholder}
          multiline={Boolean(rows)}
          minRows={rows}
          name={name}
          id={inputId}
          type={type}
          title=""
          error={Boolean(inputError)}
          value={value}
          required={Boolean(requiredMsg)}
          onChange={inputChangeHandler}
          onBlur={onBlur}
          onFocus={onTextFocus}
          inputRef={inputRef}
          inputProps={{
            maxLength,
            style: { maxHeight: maxHeight, overflow: "auto" }
          }}
          onKeyDown={onEscapeKeyDown ? onEscapeKeyDown : onKeyDown}
          autoFocus={isAutoFocus}
        />
        {inputError && <ErrorLabel message={inputError} />}
      </div>
    </>
  );
};

InputBox.propTypes = {
  type: propTypes.string,
  isAutoFocus: propTypes.bool
};

InputBox.defaultProps = {
  type: "text",
  isAutoFocus: false,
  showClearBtn: true
};

export default InputBox;
